import {
  GadgetFormInner,
  GadgetFormInnerContent,
  GadgetFormWrapper,
} from '../../V5GadgetForm/V5GadgetForm';
import { Section } from '../../MyAccount/Profile/styles';
import Typography from '../../../kingpin/atoms/Typography';
import Inputs from '../../Inputs';
import React, { useContext, useEffect, useState } from 'react';
import DatasetDefinitionsContext from '../../../contexts/DatasetDefinitionsContext';
import toSentenceCase from '../../../services/toSentenceCase';
import YAxisControls from './YAxisControls';
import { FiscalPeriodsContext } from '../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/FiscalPeriodsProvider';
import FeatureGatesContext from '../../../contexts/FeatureGatesContext';
import InfoIcon from '../../InfoIcon';
import { FISCAL_PERIODS_DISABLED_TOOLTIP } from '../../../constants';
import Row from '../../Common/Row';

const GroupVsRemainderForm = ({
  // Base
  name,
  setName,
  description,
  setDescription,
  // Misc
  isValid,
  isLoading,
  draftGadget,
  dataset,
  setDataset,
  field,
  peerGroup,
  setPeerGroup,
  minY,
  setMinY,
  maxY,
  setMaxY,
  minYBehaviour,
  setMinYBehaviour,
  maxYBehaviour,
  setMaxYBehaviour,
  setField,
  interval,
  setInterval,
  isNewGadget,
  useEntityAsScope,
  setUseEntityAsScope,
}: {
  // Base
  name: string;
  setName: React.Dispatch<React.SetStateAction<string>>;
  description: string;
  setDescription: React.Dispatch<React.SetStateAction<string>>;
  // Misc
  isValid: boolean;
  isLoading: boolean;
  interval: FleetOps.Interval | FiscalInterval;
  setInterval: React.Dispatch<
    React.SetStateAction<FleetOps.Interval | FiscalInterval>
  >;
  draftGadget?: VisualisationDefinitions.GroupVsRemainder;
  dataset: string | undefined;
  field: string | undefined;
  peerGroup: string | undefined;
  setPeerGroup: (peerGroup: string | undefined) => void;
  setDataset: (dataset: string | undefined) => void;
  setField: (field: string | undefined) => void;
  minY: number | undefined;
  setMinY: (minY: number | undefined) => void;
  maxY: number | undefined;
  setMaxY: (maxY: number | undefined) => void;
  minYBehaviour: 'auto' | 'set' | undefined;
  setMinYBehaviour: (minYBehaviour: 'auto' | 'set') => void;
  maxYBehaviour: 'auto' | 'set' | undefined;
  setMaxYBehaviour: (maxYBehaviour: 'auto' | 'set') => void;
  isNewGadget: boolean;
  useEntityAsScope: boolean;
  setUseEntityAsScope: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { datasets } = useContext(DatasetDefinitionsContext);
  const { currentFiscalYear } = useContext(FiscalPeriodsContext);
  const [datasetOptions, setDatasetOptions] = useState<DropdownOption[]>([]);
  const [fieldOptions, setFieldOptions] = useState<DropdownOption[]>([]);
  const [peerGroupOptions, setPeerGroupOptions] = useState<DropdownOption[]>(
    [],
  );
  const { isFiscalPeriodsEnabled } = useContext(FeatureGatesContext);

  useEffect(() => {
    if (!datasets) {
      return;
    }

    const newOptions = datasets.map((d) => ({
      label: d.type,
      key: d.type,
      onSelected: () => {
        setDataset(d.type);
      },
    }));

    setDatasetOptions(newOptions);
  }, [setDataset, datasets]);

  useEffect(() => {
    const selectedDataset = datasets?.find((d) => d.type === dataset);

    if (!dataset || !selectedDataset) {
      setFieldOptions([]);
      return;
    }

    const newPeerGroupOptions = selectedDataset?.fields
      .filter((f) => f.type === 'text' || f.type === 'boolean')
      .map((f) => ({
        label: f.field || '',
        key: f.field,
        onSelected: () => {
          setPeerGroup(f.field);
        },
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
    newPeerGroupOptions.unshift({
      label: 'None',
      key: 'none',
      onSelected: () => {
        setPeerGroup('');
      },
    });

    setPeerGroupOptions(newPeerGroupOptions);

    const newOptions = selectedDataset?.fields
      .filter(
        (f) => f.type === 'int' || f.type === 'long' || f.type === 'float',
      )
      .map((f) => ({
        label: f.field || '',
        key: f.field,
        onSelected: () => {
          setField(f.field);
        },
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    setFieldOptions(newOptions);
  }, [dataset, datasets, setField, setPeerGroup]);

  return (
    <GadgetFormWrapper>
      <GadgetFormInner isGadgetBuilder={false}>
        <GadgetFormInnerContent isGadgetBuilder={false}>
          <Section>
            <Typography.Body type="Label">Name</Typography.Body>
            <Inputs.TextInput
              placeholder="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid={'group-vs-remainder-name'}
            />
          </Section>
          <Section>
            <Typography.Body type="Label">Description</Typography.Body>
            <Inputs.TextInput
              placeholder="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Section>
          <Section>
            <Typography.Body type={'Label'}>Interval</Typography.Body>
            <Inputs.Dropdown
              options={[
                'auto',
                'day',
                'week',
                'month',
                'quarter',
                'year',
                'fiscalPeriod',
                'fiscalQuarter',
                'fiscalYear',
              ].map((i) => ({
                label: toSentenceCase(i),
                key: i,
                isDisabled:
                  ['fiscalPeriod', 'fiscalQuarter', 'fiscalYear'].includes(i) &&
                  (!currentFiscalYear || !isFiscalPeriodsEnabled),
                tooltip:
                  ['fiscalPeriod', 'fiscalQuarter', 'fiscalYear'].includes(i) &&
                  !isFiscalPeriodsEnabled
                    ? FISCAL_PERIODS_DISABLED_TOOLTIP
                    : undefined,
                onSelected: () => {
                  setInterval(i as FleetOps.Interval);
                },
              }))}
              selectedLabel={toSentenceCase(interval)}
            />
          </Section>
          <Section>
            <Typography.Body type="Label">Dataset</Typography.Body>
            <Inputs.Dropdown
              placeholder={'Select a dataset'}
              selectedLabel={dataset}
              options={datasetOptions}
              testId={'group-vs-remainder-dataset-picker'}
            />
          </Section>
          {dataset && (
            <>
              <Section>
                <Typography.Body type="Label">Field</Typography.Body>
                <Inputs.Dropdown
                  placeholder={'Select a field'}
                  selectedLabel={field}
                  options={fieldOptions}
                  isSearchEnabled
                  testId={'group-vs-remainder-field-picker'}
                />
              </Section>
              <Section>
                <Typography.Body type="Label">Peer Group Field</Typography.Body>
                <Inputs.Dropdown
                  placeholder={'Select a peer group field'}
                  selectedLabel={peerGroup}
                  options={peerGroupOptions}
                  isSearchEnabled
                  testId={'group-vs-remainder-peer-group-picker'}
                />
              </Section>
              <Section>
                <Row centerAlign>
                  <Typography.Body type="Label">
                    Use entity as scope
                  </Typography.Body>
                  <div style={{ marginLeft: 4 }}>
                    <InfoIcon tooltip="When enabled, the entity is applied as a scoped filter instead of using it as the 'Selected' filter." />
                  </div>
                </Row>
                <Inputs.Toggle
                  value={useEntityAsScope}
                  onChange={setUseEntityAsScope}
                />
              </Section>

              <YAxisControls
                minY={minY}
                maxY={maxY}
                minYBehaviour={minYBehaviour}
                maxYBehaviour={maxYBehaviour}
                setMinY={setMinY}
                setMaxY={setMaxY}
                setMinYBehaviour={setMinYBehaviour}
                setMaxYBehaviour={setMaxYBehaviour}
              />
            </>
          )}
        </GadgetFormInnerContent>
      </GadgetFormInner>
    </GadgetFormWrapper>
  );
};

export default GroupVsRemainderForm;
